(async (algolia) => {

  if (!customElements.get('algolia-related-products')) {

    await import('@algolia/recommend-js/dist/umd/index.js');
    const { relatedProducts } = window['@algolia/recommend-js'];
    const { default: recommend } = await import('@algolia/recommend/dist/recommend.esm.browser.js');
    const { productCardHtml, slidesHtml, slidersHtml } = await import('./modules/algolia-recommend-component.js');
    class AlgoliaRelatedProducts extends HTMLElement {
      constructor() {
        super();

        // Options
        this.options = {
          containerId: null,
          indexName: `${algolia.config.index_prefix}products`,
          variantIds: [],
          itemsToShow: 10,
          confidenceScore: 0, // 0-100 (0 = low confidence, 100 = high confidence)
          randomize: true,
          cartItemIds: [],
        }

        // Get options from element data and combine with this.options
        if (this?.dataset?.options) {
          const dataOptions = parseJson(this.dataset.options);
          this.options = {
            ...this.options,
            ...dataOptions
          };
        }

        // Required options to initialize
        if (!this.options.containerId || !this.options.indexName || !this.options.variantIds.length) {
          return;
        }

        // Format options
        this.options.variantIds = this.options.variantIds.split(',').map(id => String(id).trim());
        this.options.itemsToShow = parseInt(this.options.itemsToShow);
        this.options.confidenceScore = parseFloat(this.options.confidenceScore);
        this.options.cartItemIds = this.options.cartItemIds.map(id => parseInt(id));

        // Initialize Swym objects if they don't exist (same as collection component)
        if (typeof window !== 'undefined') {
          const swymObjects = ['SwymViewProducts', 'SwymWatchProducts', 'SwymProductVariants'];
          swymObjects.forEach(k => window[k] || (window[k] = {}));
        }

        // Initialize Algolia Recommend
        this.recommendClient = recommend(
          algolia.config.app_id,
          algolia.config.search_api_key,
        );

        // Initialize recommendations
        this.initRecommendations();

        // Reinitalize recommendations on section load
        if (Shopify.designMode) {
          window.addEventListener('shopify:section:load', (event) => {
            this.initRecommendations();
          });
        }

      }

      initRecommendations() {
        const { recommendClient } = this;
        const { containerId, indexName, variantIds, itemsToShow, randomize, confidenceScore, cartItemIds } = this.options;

        // Use a simpler approach - directly fetch and render products
        this.fetchAndRenderProducts();

      }

      async fetchAndRenderProducts() {
        const { recommendClient } = this;
        const { containerId, indexName, variantIds, itemsToShow, randomize, confidenceScore, cartItemIds } = this.options;

        try {
          // Fetch related products directly
          const response = await recommendClient.getRelatedProducts([{
            indexName,
            objectID: variantIds[0], // Use the first variant as the main product
            maxRecommendations: 30,
            threshold: confidenceScore,
            queryParameters: {
              filters: "inventory_available:true"
            }
          }]);

          let items = response.results[0].hits || [];

          // Process items
          items = items.sort((a, b) => b._score - a._score);
          items = items.filter(item => !cartItemIds.includes(item.id));
          items = items.slice(0, itemsToShow);

          if (randomize) {
            items = items.sort((a, b) => 0.5 - Math.random());
          }

          if (items.length === 0) {
            console.log('No related products found');
            return;
          }

          // Show the container and hide initial content
          const recommendedContainer = this.querySelector('[data-recommended-items]');
          const initialContainer = this.querySelector('[data-initial-content]');

          if (recommendedContainer) {
            recommendedContainer.classList.remove('hidden');
          }
          if (initialContainer) {
            initialContainer.classList.add('hidden');
          }

          // Render products
          this.renderProducts(items);

        } catch (error) {
          console.error('Error fetching related products:', error);
        }
      }

      renderProducts(items) {
        const container = this.querySelector('[data-recommended-items]');
        if (!container) return;

        // Clear existing content
        container.innerHTML = '';

        // Create a proper HTML template function
        const html = (strings, ...values) => {
          return strings.reduce((result, string, i) => {
            return result + string + (values[i] || '');
          }, '');
        };

        // Create the full slider structure
        const sliderHtml = `
          <section class="auc-Recommend related-products-items slider__content slider__content--full slider-scroller-content">
          ${items.map(item => {
            const productHtml = productCardHtml({ item, html, element: this });
            return `<div class="slider__slide related-products-item" data-slider-slide>${productHtml}</div>`;
          }).join('')}
          </section>
        `;

        // Insert the HTML directly
        container.insertAdjacentHTML('beforeend', sliderHtml);

        // Add event listeners for click tracking
        this.addClickEventListeners();
      }

      addClickEventListeners() {
        // Add click tracking for product links
        const productLinks = this.querySelectorAll('a[data-object-id]');
        productLinks.forEach(link => {
          link.addEventListener('click', (e) => {
            const objectId = e.currentTarget.getAttribute('data-object-id');
            if (window.AA && window.AA.clickedOnProduct) {
              window.AA.clickedOnProduct(objectId);
            }
          });
        });

        // Wishlist functionality is handled by global event delegation in algolia-collection.js
      }

    }

    window.AlgoliaRelatedProducts = AlgoliaRelatedProducts;

    customElements.define('algolia-related-products', AlgoliaRelatedProducts);

  }

})(window.algoliaShopify);
